import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import KundaliChart from "../components/KundaliChart";
import {
  Sparkle, Calculator, ArrowLeft, Sun, Crown, Star, TrendUp, TrendDown,
  Compass, Calendar, Warning, CheckCircle, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useSavedReportLoader } from "../lib/useSavedReport";
import { useAuth } from "../context/AuthContext";
import { useToolUnlock } from "../lib/toolUnlock";

const CURRENT_YEAR = new Date().getFullYear();

export default function VarshaphalPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    date: "", time: "", place: "", timezone: "Asia/Kolkata",
    target_year: CURRENT_YEAR,
  });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);  // birth-form completed
  const { user } = useAuth();

  // Server-side paywall guards both `/varshaphal/compute` (no — compute is
  // still free math) and `/varshaphal/narrative` (yes — Universal-Key
  // spend). We mirror that here so we NEVER compute or fetch until the
  // user is unlocked — "no teasers" per product decision (2026-07-28).
  const birthDetails = { date: form.date, time: form.time, place: form.place, timezone: form.timezone };
  const hasBirth = !!(form.date && form.time && form.place);
  const { unlocked } = useToolUnlock({
    tool: "varshaphal",
    birthDetails: hasBirth ? birthDetails : null,
    user,
  });

  useSavedReportLoader({
    expectedTool: "varshaphal",
    setForm: (bd) => {
      if (bd?.date) setForm((f) => ({ ...f, date: bd.date }));
      if (bd?.time) setForm((f) => ({ ...f, time: bd.time }));
      if (bd?.place) setForm((f) => ({ ...f, place: bd.place }));
      if (bd?.timezone) setForm((f) => ({ ...f, timezone: bd.timezone }));
    },
    setResult: (r) => {
      setResult(r);
      // Only accept a saved narrative that has real content. An empty
      // object (e.g. from a botched earlier save) would otherwise
      // suppress the auto-fetch and leave the section blank.
      if (r?.narrative && Object.keys(r.narrative).length > 0 && r.narrative.opening) {
        setNarrative(r.narrative);
      }
    },
  });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const runCompute = async () => {
    if (busy || result) return;
    setBusy(true);
    setNarrative(null);
    try {
      const { data } = await api.post("/varshaphal/compute", {
        date: form.date, time: form.time, place: form.place,
        timezone: form.timezone,
        target_year: Number(form.target_year) || CURRENT_YEAR,
      });
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='vp-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your Varshaphal. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  // Compute the varshaphal ONLY once the user is unlocked. Both the
  // deterministic chart AND the Claude narrative are gated — nothing
  // renders until payment lands.
  useEffect(() => {
    if (submitted && unlocked && !result && !busy) runCompute();
  }, [submitted, unlocked]);

  const analyze = (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setSubmitted(true);
    // If already unlocked, effect above fires runCompute(). Otherwise the
    // standalone paywall CTA is shown.
  };

  // Fetch the AI narrative lazily after unlock
  const fetchNarrative = async () => {
    if (!result || narrative || narrativeBusy) return;
    setNarrativeBusy(true);
    try {
      const { data } = await api.post("/varshaphal/narrative", {
        report: result,
        birth_details: { date: form.date, time: form.time, place: form.place, timezone: form.timezone },
      });
      setNarrative(data.narrative);
      // Persist the narrative into the saved report by updating the report state
      // (the saved version on backend already includes whatever we send through unlock)
      setResult((r) => ({ ...(r || {}), narrative: data.narrative }));
    } catch (err) {
      toast.error("Could not generate the AI yearly narrative. The chart insights are still saved.");
    } finally {
      setNarrativeBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="varshaphal-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="vp-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Sun size={14} weight="duotone" /> Premium · Varshaphal Annual Reading
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Varshaphal · <span className="italic text-gold">your year, decoded</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          Tajaka system. We cast a fresh chart for the moment transit Sun returns to its natal place
          this year, then layer in your <b className="text-[#3F2D1B]">Muntha</b>, the strongest{" "}
          <b className="text-[#3F2D1B]">Year-Lord (Varshesh)</b>, 23 classical <b className="text-[#3F2D1B]">Sahams</b>{" "}
          (sensitive points), and Tajaka yogas that flag sudden gains and job-loss risk.
          The complete annual reading — chart, sahams, combos and AI yearly narrative — unlocks once for this chart, forever.
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="vp-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="vp-time" />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Mumbai, India"
                  testId="vp-place"
                />
              </div>
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Year of analysis</span>
              <select value={form.target_year} onChange={handle("target_year")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="vp-year">
                {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="vp-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Casting your Varshaphal..." : "Cast my yearly chart"}
          </button>
        </form>

        {/* Standalone paywall — birth submitted but not yet unlocked. No
            teasers, no math, no LLM — the entire reading is hidden until
            payment lands (per product decision 2026-07-28). */}
        {submitted && !unlocked && (
          <div className="mt-10" data-testid="vp-paywall-only">
            <PaywallGate
              tool="varshaphal"
              toolLabel="Varshaphal · Annual Reading"
              birthDetails={{ date: form.date, time: form.time, place: form.place, timezone: form.timezone }}
              variant="standalone"
            />
          </div>
        )}

        {/* Loading state while compute runs (post-unlock) */}
        {submitted && unlocked && busy && !result && (
          <div className="mt-10 text-center animate-pulse text-muted text-sm" data-testid="vp-loading">
            Casting your solar-return chart…
          </div>
        )}

        {/* Result — only rendered once the user is unlocked & compute finished */}
        {result && (
          <div className="mt-10 space-y-6" data-testid="vp-result">
            <VpHero result={result} />

            {/* Varsha Lagna — THE headline of the year */}
            <LagnaReadCard lagnaRead={result.lagna_read} />

            {/* Solar Return chart + Muntha + Year-Lord */}
            <div className="card-surface rounded-2xl p-5 sm:p-7">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                <Sparkle size={14} weight="duotone" /> Your Varsha (Solar-Return) chart
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
                <div className="text-center" data-testid="vp-chart">
                  <div className="label-eyebrow !text-[10px] text-gold mb-2">
                    {result.target_year} · Lagna {result.varsha.lagna.rashi}
                  </div>
                  <div className="inline-block">
                    <KundaliChart kundali={result.varsha} size={280} />
                  </div>
                  <div className="text-[10px] mt-2 text-muted">
                    {result.varsha_chart_meta?.is_day_birth ? "Day-time solar return" : "Night-time solar return"}
                  </div>
                </div>
                <div className="space-y-4">
                  <MunthaCard muntha={result.muntha} />
                  <YearLordCard yearLord={result.year_lord} />
                </div>
              </div>
            </div>

            {/* Full unlocked section — narrative + all combos */}
            <UnlockedSection
              key={result.target_year}
              result={result}
              narrative={narrative}
              narrativeBusy={narrativeBusy}
              fetchNarrative={fetchNarrative}
            />

            {/* CTA */}
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 sm:p-7 text-center">
              <p className="text-sm text-amber-950">
                Want an astrologer to walk you through your Varshaphal, dasha hits and remedies live?
              </p>
              <button
                onClick={() => navigate("/astrologers")}
                className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium mt-3"
                data-testid="vp-cta-astrologers"
              >
                Talk to an astrologer →
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// Auto-fetch narrative once unlocked (rendered only when result is
// available AND the user has paid — so the narrative call is always
// scoped to a legitimate unlocked user).
function UnlockedSection({ result, narrative, narrativeBusy, fetchNarrative }) {
  useEffect(() => {
    if (!narrative && !narrativeBusy) fetchNarrative();
  }, []);
  return (
    <div className="space-y-6">
      {/* Focus-energy bar graph — where to spend your year */}
      <FocusBarsCard scores={result.focus_scores} />

      {/* Strengths & Growth-areas */}
      <StrengthsGrowthCard strengths={result.strengths} growthAreas={result.growth_areas} />

      {/* AI narrative */}
      <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="vp-narrative">
        <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
          <Lightning size={14} weight="duotone" /> Your year, in plain words
        </div>
        {narrativeBusy && !narrative && (
          <div className="text-sm text-muted italic">Reading your year through Tajaka rules…</div>
        )}
        {narrative && <NarrativeBlocks narrative={narrative} />}
      </div>

      {/* All combos */}
      {result.combos.sudden_gains.length > 0 && (
        <div className="space-y-3" data-testid="vp-gains">
          <div className="label-eyebrow text-emerald-700 flex items-center gap-1.5">
            <TrendUp size={14} weight="duotone" /> Sudden-gain yogas this year
          </div>
          {result.combos.sudden_gains.map((c) => (
            <ComboCard key={c.key} combo={c} positive />
          ))}
        </div>
      )}
      {result.combos.setbacks.length > 0 && (
        <div className="space-y-3" data-testid="vp-setbacks">
          <div className="label-eyebrow text-rose-700 flex items-center gap-1.5">
            <TrendDown size={14} weight="duotone" /> Setback flags · be defensive
          </div>
          {result.combos.setbacks.map((c) => (
            <ComboCard key={c.key} combo={c} />
          ))}
        </div>
      )}

      {/* Sahams table */}
      <div className="card-surface rounded-2xl p-5 sm:p-6" data-testid="vp-sahams">
        <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
          <Compass size={14} weight="duotone" /> Sahams · sensitive points of the year
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {result.sahams.map((s) => (
            <div key={s.key} className="rounded-lg px-3 py-2 bg-white/70 border border-soft" data-testid={`vp-saham-${s.key}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium text-[#3F2D1B]">{s.label}</div>
                <div className="text-[10px] text-gold">H{s.house}</div>
              </div>
              <div className="text-[11px] text-muted leading-tight">
                {s.rashi} · {s.nakshatra} · lord {s.rashi_lord}
              </div>
              <div className="text-[10px] text-[#3F2D1B]/70 mt-1 italic">{s.meaning}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 12-house forecast */}
      <div className="card-surface rounded-2xl p-5 sm:p-6" data-testid="vp-forecast">
        <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
          <Calendar size={14} weight="duotone" /> 12-house forecast for the year
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {result.forecast_12h.map((f) => {
            const toneClass = f.tone === "auspicious" ? "bg-emerald-50 border-emerald-200" :
                              f.tone === "challenging" ? "bg-rose-50 border-rose-200" :
                              "bg-white/70 border-soft";
            return (
              <div key={f.house} className={`rounded-lg px-3 py-2 border ${toneClass}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-medium text-[#3F2D1B]">H{f.house} · {f.domain}{f.muntha_here ? " ★" : ""}</div>
                  <div className="text-[10px] capitalize text-[#3F2D1B]/70">{f.tone}</div>
                </div>
                <div className="text-[11px] text-muted leading-tight">
                  Lord <b className="text-[#3F2D1B]">{f.cusp_lord}</b> in {f.lord_house}H · {f.lord_dignity}
                </div>
                <div className="text-[10px] text-[#3F2D1B]/70 mt-1 italic">{f.tone_text}</div>
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-muted mt-2 italic">★ = your Muntha sits here this year — extra focus.</div>
      </div>
    </div>
  );
}

function FocusBarsCard({ scores }) {
  if (!scores?.length) return null;
  const top = scores[0];
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="vp-focus-bars">
      <div className="flex items-baseline gap-2 mb-1">
        <div className="label-eyebrow text-gold flex items-center gap-1.5">
          <Lightning size={14} weight="duotone" /> Where to focus your energy this year
        </div>
      </div>
      <p className="text-xs sm:text-sm text-muted mb-5">
        Each life-area scored 0–100 from your Varsha chart (house tones, sahams, muntha + detected yogas).{" "}
        <b className="text-[#3F2D1B]">Lean hardest into the top bars</b> — that&apos;s where your year wants you to spend energy.
      </p>
      <div className="space-y-3.5">
        {scores.map((s, i) => (
          <FocusBar key={s.key} item={s} highlight={i === 0} />
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-soft text-xs sm:text-sm text-[#3F2D1B]">
        <b className="text-gold">🎯 Your #1 focus this year:</b>{" "}
        <span className="font-medium">{top.label}</span> ({top.score}/100) — {top.tagline}
      </div>
    </div>
  );
}

function FocusBar({ item, highlight }) {
  return (
    <div data-testid={`vp-focus-${item.key}`}>
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium text-[#3F2D1B] ${highlight ? "font-bold" : ""}`}>
            {item.label}
          </span>
          {highlight && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FFB800] text-[#3F2D1B] font-bold leading-none">
              TOP FOCUS
            </span>
          )}
        </div>
        <span className="text-xs tabular-nums font-medium text-[#3F2D1B]/80">{item.score}/100</span>
      </div>
      <div className="h-3 rounded-full bg-[#F1E9D8] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${item.score}%`,
            background: `linear-gradient(90deg, ${item.color}, ${item.color}cc)`,
          }}
        />
      </div>
      <div className="text-[11px] text-muted mt-1 italic leading-snug">{item.tagline}</div>
    </div>
  );
}

function StrengthsGrowthCard({ strengths, growthAreas }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="vp-strengths-growth">
      {/* Strengths */}
      <div className="rounded-2xl p-5 sm:p-7 bg-emerald-50 border border-emerald-200 shadow-sm" data-testid="vp-strengths-card">
        <div className="label-eyebrow text-emerald-700 flex items-center gap-1.5 mb-3">
          <CheckCircle size={14} weight="duotone" /> Strengths of your year · double down here
        </div>
        <div className="space-y-3.5">
          {strengths?.length > 0 ? strengths.map((s, i) => (
            <div key={i} className="rounded-xl bg-white/80 border border-emerald-200/60 p-3" data-testid={`vp-strength-${i}`}>
              <div className="flex items-start gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-emerald-950">{s.title}</div>
                  <div className="text-xs text-emerald-900/85 mt-1 leading-relaxed">{s.detail}</div>
                </div>
              </div>
            </div>
          )) : (
            <div className="text-xs text-emerald-900/70 italic">Quiet strength year — go inward.</div>
          )}
        </div>
      </div>
      {/* Growth areas */}
      <div className="rounded-2xl p-5 sm:p-7 bg-amber-50 border border-amber-200 shadow-sm" data-testid="vp-growth-card">
        <div className="label-eyebrow text-amber-800 flex items-center gap-1.5 mb-3">
          <Warning size={14} weight="duotone" /> Growth areas · what to actively work on
        </div>
        <div className="space-y-3.5">
          {growthAreas?.length > 0 ? growthAreas.map((g, i) => (
            <div key={i} className="rounded-xl bg-white/80 border border-amber-200/60 p-3" data-testid={`vp-growth-${i}`}>
              <div className="flex items-start gap-2">
                <Warning size={16} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-950">{g.title}</div>
                  <div className="text-xs text-amber-900/85 mt-1 leading-relaxed">{g.detail}</div>
                  {g.action && (
                    <div className="mt-2 text-xs text-amber-950/95 leading-relaxed">
                      <b className="text-amber-800">→ Action:</b> {g.action}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )) : (
            <div className="text-xs text-amber-900/70 italic">No major flags this year — keep gliding.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function VpHero({ result }) {
  const m = result.muntha;
  const yl = result.year_lord;
  const lr = result.lagna_read;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40" data-testid="vp-hero">
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
        <Sun size={14} weight="fill" /> {result.target_year} · {result.year_of_life_label}
      </div>
      <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
        Varsha Lagna <span className="italic text-amber-300">{lr?.lagna_rashi || result.varsha.lagna.rashi}</span>
        {lr?.theme_title && (
          <span className="block text-amber-200/80 text-base font-light mt-1">
            {lr.theme_title}
          </span>
        )}
      </h2>
      <div className="mt-3 text-amber-100/90 text-sm sm:text-base leading-relaxed">
        Your Muntha sits in <b className="text-amber-300">{m.rashi}</b> (the {m.house_in_varsha}
        <sup>{m.house_in_varsha === 1 ? "st" : m.house_in_varsha === 2 ? "nd" : m.house_in_varsha === 3 ? "rd" : "th"}</sup> house of your Varsha chart),
        ruled by <b className="text-amber-300">{m.rashi_lord}</b>. Year-lord strength:{" "}
        <b className="text-amber-300">{yl.score}/10</b>.
      </div>
    </div>
  );
}

function LagnaReadCard({ lagnaRead }) {
  if (!lagnaRead) return null;
  const lr = lagnaRead;
  const toneClass = lr.tone === "auspicious"
    ? "bg-emerald-50 border-emerald-200"
    : lr.tone === "challenging"
    ? "bg-rose-50 border-rose-200"
    : "bg-amber-50 border-amber-200";
  return (
    <div className={`rounded-2xl p-5 sm:p-7 border ${toneClass}`} data-testid="vp-lagna-read">
      <div className="flex items-center gap-2 text-gold label-eyebrow mb-3">
        <Sparkle size={14} weight="duotone" /> Varsha Lagna · the headline of your year
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 items-start">
        {/* Theme block — takes 2 cols */}
        <div className="sm:col-span-2">
          <div className="flex items-baseline gap-2 flex-wrap mb-1">
            <h3 className="font-serif-display text-2xl sm:text-3xl text-[#3F2D1B]">
              {lr.lagna_rashi}
            </h3>
            <span className="text-xs text-muted">rises this year</span>
          </div>
          <div className="text-sm font-medium text-[#3F2D1B] mb-1">{lr.theme_title}</div>
          <p className="text-sm text-[#3F2D1B]/85 leading-relaxed">{lr.theme_detail}</p>
        </div>
        {/* Right column: lagnesh quick stat */}
        {lr.lagnesh && (
          <div className="rounded-xl bg-white/70 border border-soft px-3 py-3" data-testid="vp-lagnesh-stat">
            <div className="label-eyebrow !text-[10px] text-gold mb-1">Varsha Lagnesh</div>
            <div className="font-serif-display text-xl text-[#3F2D1B]">
              {lr.lagnesh.planet}
              {lr.lagnesh.retrograde && <span className="text-[10px] text-rose-700 ml-1">℞</span>}
            </div>
            <div className="text-xs text-muted mt-0.5">
              in <b className="text-[#3F2D1B]">{lr.lagnesh.house}H</b> · {lr.lagnesh.rashi}
            </div>
            <div className="text-[11px] text-[#3F2D1B]/70 mt-1 italic capitalize">{lr.lagnesh.dignity}</div>
          </div>
        )}
      </div>

      {/* Lagnesh placement note */}
      {lr.lagnesh?.note && (
        <div className="mt-4 pt-4 border-t border-soft text-sm text-[#3F2D1B]/90 leading-relaxed">
          <b className="text-gold">Lagnesh in {lr.lagnesh.house}H:</b> {lr.lagnesh.note}
        </div>
      )}

      {/* Planets in lagna */}
      {lr.planets_in_lagna?.length > 0 ? (
        <div className="mt-4 pt-4 border-t border-soft">
          <div className="label-eyebrow !text-[10px] text-gold mb-2">Planets sitting in your Varsha 1st house</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {lr.planets_in_lagna.map((p) => (
              <div key={p.planet} className="rounded-lg bg-white/70 border border-soft px-3 py-2" data-testid={`vp-lagna-planet-${p.planet}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-medium text-sm text-[#3F2D1B]">
                    {p.planet}
                    {p.retrograde && <span className="text-[10px] text-rose-700 ml-1">℞</span>}
                  </div>
                  <div className="text-[10px] text-muted capitalize">{p.dignity}</div>
                </div>
                {p.note && <div className="text-[11px] text-[#3F2D1B]/80 mt-1 leading-snug">{p.note}</div>}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t border-soft text-xs text-muted italic">
          No planets sit in your Varsha 1st house this year — the lagna theme flows pure, undisturbed.
        </div>
      )}
    </div>
  );
}

function MunthaCard({ muntha }) {
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 sm:p-5" data-testid="vp-muntha-card">
      <div className="flex items-center gap-2 text-amber-900 text-[10px] uppercase tracking-widest mb-2">
        <Star size={14} weight="duotone" /> Muntha · the year&apos;s sensitive sign
      </div>
      <div className="text-2xl font-serif-display text-[#3F2D1B]">{muntha.rashi}</div>
      <div className="text-xs text-muted mt-1">House {muntha.house_in_varsha} of your Varsha chart</div>
      <div className="text-xs mt-2 text-[#3F2D1B]">
        Munthesha (year-significator): <b>{muntha.rashi_lord}</b>
      </div>
    </div>
  );
}

function YearLordCard({ yearLord }) {
  return (
    <div className="rounded-2xl bg-[#3F2D1B]/95 text-[#FAF4E8] p-4 sm:p-5 border border-amber-600/40" data-testid="vp-yearlord-card">
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
        <Crown size={14} weight="duotone" /> Year-Lord (Varshesh)
      </div>
      <div className="text-2xl font-serif-display">{yearLord.planet}</div>
      <div className="text-xs text-amber-100/80 mt-1">Chosen as {yearLord.role} · strength {yearLord.score}/10</div>
      <div className="text-[11px] text-amber-100/70 mt-2 italic leading-relaxed">
        {yearLord.reasons.join(" · ")}
      </div>
    </div>
  );
}

function ComboCard({ combo, positive = false }) {
  return (
    <div className={`rounded-2xl p-4 sm:p-5 border ${
      positive
        ? "bg-emerald-50 border-emerald-200"
        : "bg-rose-50 border-rose-200"
    }`} data-testid={`vp-combo-${combo.key}`}>
      <div className="flex items-start gap-2">
        {positive
          ? <CheckCircle size={18} weight="fill" className="text-emerald-600 flex-shrink-0" />
          : <Warning size={18} weight="fill" className="text-rose-600 flex-shrink-0" />}
        <div>
          <div className={`text-sm font-medium ${positive ? "text-emerald-900" : "text-rose-900"}`}>
            {combo.title}
          </div>
          <div className={`text-xs mt-1 ${positive ? "text-emerald-800" : "text-rose-800"} leading-relaxed`}>
            {combo.detail}
          </div>
        </div>
      </div>
    </div>
  );
}

function NarrativeBlocks({ narrative }) {
  const sections = [
    ["opening", null],
    ["muntha_read", "What your Muntha says"],
    ["year_lord_read", "Your Year-Lord's lesson"],
    ["money", "Money"],
    ["career", "Career"],
    ["love_family", "Love & family"],
    ["health", "Health"],
    ["lucky_breaks", "Lucky breaks"],
    ["warnings", "Be careful with"],
    ["monthly_arc", "How the year flows"],
    ["closing_blessing", null],
  ];
  return (
    <div className="space-y-4 text-sm sm:text-base text-[#3F2D1B] leading-relaxed">
      {sections.map(([k, label]) => {
        const text = narrative[k];
        if (!text) return null;
        return (
          <div key={k}>
            {label && (
              <div className="font-serif-display text-base text-gold mb-1">{label}</div>
            )}
            <p className="font-light">{text}</p>
          </div>
        );
      })}
      {narrative.top_actions?.length > 0 && (
        <div className="pt-3 mt-3 border-t border-soft">
          <div className="label-eyebrow text-emerald-700 mb-2">Do these</div>
          <ul className="space-y-1.5">
            {narrative.top_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-emerald-950">
                <CheckCircle size={14} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {narrative.top_avoid?.length > 0 && (
        <div className="pt-3 mt-3 border-t border-soft">
          <div className="label-eyebrow text-rose-700 mb-2">Avoid these</div>
          <ul className="space-y-1.5">
            {narrative.top_avoid.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-rose-950">
                <Warning size={14} weight="fill" className="text-rose-600 flex-shrink-0 mt-0.5" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
